import { createSelector } from '@ngrx/store';

import { isEqual, uniq } from 'lodash';
import { EventLocationTypes, locationNameMap } from 'src/app/global.constants';
import { validateLocationsForEventType } from 'src/app/job/job-helpers';
import { createConfigSelector } from 'src/app/state/branding.store';

import { dayjs } from '../../../../core/dayjs/dayjs';
import { bookingScheduleFeature } from '../../../../schedules/booking-schedule/booking-schedule.feature';
import { EventStatuses } from '../../../../schedules/dispatch/store/dispatch.reducer';
import { ExtendedCalendarEventForScheduleFragment } from '../../../../services/full-calendar-helper.service';
import { convertSecondsToTimeUnit } from '../../../../shared/seconds-to-time-unit.pipe';
import { MS_ONE_SECOND, S_ONE_MINUTE } from '../../../../time';
import { LOCATION_ICON_MAP } from '../../../job-events-helpers';
import { 
  selectJob,
  selectJobTimezone,
} from '../../../job-tool.reducer';

import { eventItineraryFeature } from './event-itinerary/event-itinerary.feature';
import { timelineBookEventFeature } from './timeline-book-event.feature';

export const selectStartTimeInterval = createConfigSelector('FIND_STARTTIMEINTERVAL');
export const selectDefaultForIncludeDockTravel = createConfigSelector('CALENDAREVENTS_INCLUDEDOCKTRAVELTIME');
export const selectShowDockTravelTimeCheckbox = createConfigSelector('CALENDAREVENTS_DOCKTRAVELTIMECHECKBOX');

export const selectEventToBook = createSelector(
    timelineBookEventFeature.selectBookEventDialog,
    selectJob,
    ({ eventId }, job) => job?.events?.find((e) => e.id === eventId),
);

export const selectEventMissingLocations = createSelector(
  selectJob,
  selectEventToBook,
  (job, event) => {
    if (!event) return [];
    const validation = validateLocationsForEventType(job?.locations || [], event.type);
    return [...validation.missingLocations, ...validation.invalidLocations];
  }
);

export const selectEventsWithMissingAndInvalidLocations = createSelector(
  selectJob,
  (job) => {
    if (!job?.events) return {};
    const result: Record<string, string[]> = {};
    for (const event of job.events) {
      const validation = validateLocationsForEventType(job.locations || [], event.type);
      if (!validation.isValid) {
        result[event.id] = [...validation.missingLocations, ...validation.invalidLocations];
      }
    }
    return result;
  }
);

const selectAvailableTimes = createSelector(
  timelineBookEventFeature.selectBookEventDialog,
  selectStartTimeInterval,
  selectJobTimezone,
  ({ windows, restrictionsDisabled }, interval, timezone) => {

    if (restrictionsDisabled) {
      const today = dayjs.tz(dayjs(), timezone);
      const start = today.startOf('day').unix();
      const end = today.endOf('day').unix();
      const times: number[] = [];

      interval = interval || 15 * S_ONE_MINUTE;

      for (let time = start; time < end; time += interval) {
        times.push(time);
      }

      return times.map((t) => dayjs.unix(t).format('h:mm A'));
    }

    return uniq(windows.flatMap((w) => w.startTimes))
      .map((t) => dayjs.tz(t * MS_ONE_SECOND, timezone).format('h:mm A'));
  }
);

export const selectStartDayjs = (input?: { defaultTime: string }) => createSelector(
  timelineBookEventFeature.selectBookEventDialog,
  selectJobTimezone,
  ({ date, time }, timezone) => {
    if (!date) { return; }
    if (!time && !input?.defaultTime) { return; }
    time = time || input?.defaultTime;
    return dayjs.tz(`${date} ${time}`, 'YYYY-MM-DD h:mm A', timezone);
  }
);

const selectAvailableWindows = createSelector(
  selectStartDayjs(),
  timelineBookEventFeature.selectBookEventDialog,
  (startDayjs, { windows }) => {
    if (!startDayjs) { return windows; }
    return windows.filter((w) => w.startTimes.includes(startDayjs.unix()));
  }
);

const selectAvailableAssets = createSelector(
    bookingScheduleFeature.selectBookingSchedule,
    timelineBookEventFeature.selectBookEventDialog,
    selectAvailableWindows,
    ({ assets }, { restrictionsDisabled }, availableWindows) => {

      if (restrictionsDisabled) return assets;
      return uniq(availableWindows.flatMap((w) => w.assets));
    },
);

export const selectEventItineraryLocations = createSelector(
  eventItineraryFeature.selectEventItinerary,
  selectStartDayjs({ defaultTime: '8:00 AM'}),
  selectJobTimezone,
  (eventItinerary, start, timezone) => {

    const locations = Object.values(eventItinerary.locations).sort((a, b) => a.order - b.order);

    if (!locations.length) { return []; }

    if (!start) { return []; }

    let currentTime = start.unix();

    const dockStart = locations.find((l) => l.locationType === EventLocationTypes.dockStart);

    if (dockStart) {
      currentTime = currentTime - dockStart.totalTravelTimeToNextLocation - dockStart.estimatedTimeAtLocation;
    }

    return locations.map((location) => {

      const arrivalTime = currentTime;

      currentTime += location.estimatedTimeAtLocation;

      const departureTime = currentTime;

      currentTime += location.totalTravelTimeToNextLocation;

      const arrivalTimeDayJs = dayjs.tz(arrivalTime * 1000, timezone);
      const departureTimeDayJs = dayjs.tz(departureTime * 1000, timezone);

      return {
        ...location,
        name: location.locationType ? (locationNameMap[location.locationType] || location.locationType) : 'unassigned',
        icon: LOCATION_ICON_MAP[location.locationType] || LOCATION_ICON_MAP.start,
        arrivalTimeEpoch: arrivalTimeDayJs.unix(),
        departureTimeEpoch: departureTimeDayJs.unix(),
        arrivalTime: formatLocationTime(arrivalTimeDayJs),
        departureTime: formatLocationTime(departureTimeDayJs),
        duration: formatDuration({ arrival: arrivalTimeDayJs.unix(), departure: departureTimeDayJs.unix() }),
        // Used to get resource changes
        type: location.locationType,
      }
    });
  }
);

const selectEventItineraryNoLocations = createSelector(
  selectEventItineraryLocations,
  (locations) => {
    return {
      noLocations: locations?.length === 0,
      noLocationsWarning: 'There are no locations on the event. Add one to book this event.'
    };
  },
);

function formatLocationTime(time: dayjs.Dayjs) {
  if (time.format('m') === '0') {
    return time.format('ha');
  }
  return time.format('h:mma');
}

function formatDuration({ arrival, departure }: { arrival: number; departure: number }) {
  if (arrival === departure) {
    return '0 min';
  }
  return convertSecondsToTimeUnit(departure - arrival, { compact: true }).trim();
}

export const selectEventItineraryTotals = createSelector(
  eventItineraryFeature.selectEventItinerary,
  ({ locations }) => {

    let totalLocationTime = 0;
    let totalTravelTime = 0;
    let totalDistanceMeters = 0;

    for (const location of Object.values(locations)) {
        totalLocationTime += location.estimatedTimeAtLocation || 0;
        totalTravelTime += location.totalTravelTimeToNextLocation || 0;
        totalDistanceMeters += location.distanceToNextLocation || 0;
    }

    return {
      totalLocationTime,
      totalTravelTime,
      totalDistanceMeters,
      totalTime: totalLocationTime + totalTravelTime,
    }
  }
);

export const selectTotalLocationTime = createSelector(
  selectEventItineraryTotals,
  (totals) => {
    return totals?.totalLocationTime;
  }
);

export const selectTotalTravelTime = createSelector(
  selectEventItineraryTotals,
  (totals) => {
    return totals?.totalTravelTime;
  },
);

export const selectTotalTime = createSelector(
  selectEventItineraryTotals,
  (totals) => {
    return totals?.totalTime;
  },
);

export const selectItinerarySummary = createSelector(
  selectEventItineraryTotals,
  (totals) => {
    const { totalLocationTime, totalTravelTime, totalTime } = totals || {};

    if (!totalTime) {
      return null;
    }

    // Minimum width for visibility
    const minWidth = 20;
    let laborTimeWidth = ((totalLocationTime / totalTime) * 100);
    let travelTimeWidth = 100 - laborTimeWidth;

    // Ensure minimum width for both bars
    if (laborTimeWidth < minWidth) {
      laborTimeWidth = minWidth;
      travelTimeWidth = 100 - laborTimeWidth;
    } else if (travelTimeWidth < minWidth) {
      travelTimeWidth = minWidth;
      laborTimeWidth = 100 - travelTimeWidth;
    }

    return {
      laborTimeWidth,
      travelTimeWidth,
      totalLocationTime: `${convertSecondsToTimeUnit(totalLocationTime).trim()} labor`,
      totalTravelTime: `${convertSecondsToTimeUnit(totalTravelTime).trim()} travel`,
      totalTime: `${convertSecondsToTimeUnit(totalTime)} Total Duration`,
    };
  }
);

export const selectPlaceholderEvent = createSelector(
  selectEventToBook,
  selectTotalTime,
  selectAvailableAssets,
  selectEventItineraryLocations,
  selectStartDayjs(),
  timelineBookEventFeature.selectBookEventDialog,
  bookingScheduleFeature.selectBookingSchedule,
  (
    event,
    totalTime,
    availableAssets,
    itineraryLocations,
    startDayjs,
    {
      assetIds: selectedAssetIds,
      scheduleMode,
    },
    bookingSchedule,
  ): ExtendedCalendarEventForScheduleFragment => {
    if (!event) {
      return event;
    }

    if (scheduleMode === 'view') {
      return event;
    }

    // Requirement: Normalize selectedAssetIds to avoid isEqual(undefined, []) false positives
    const normalizedSelectedAssetIds = selectedAssetIds ?? [];

    // Requirement: Resolve selected assets from available assets first, falling back to all
    // schedule assets to preserve selection during availability refresh
    const allAssets = bookingSchedule.assets || [];
    const selectedAssets = normalizedSelectedAssetIds
      .map((id) => availableAssets.find((a) => a.id === id) || allAssets.find((a) => a.id === id))
      .filter(Boolean);

    // Requirement: Before timing data loads, keep placeholder state aligned with current selected
    // assets so the calendar event reflects user intent
    if (!startDayjs || !totalTime) {
      const eventAssetIds = event?.assets?.map((a) => a.id) || [];
      const hasAssetChanges = !isEqual(normalizedSelectedAssetIds, eventAssetIds);
      const isNotBooked = event.status !== EventStatuses.Booked;

      if (!hasAssetChanges && !isNotBooked) {
        return event;
      }

      return {
        ...event,
        assets: selectedAssets,
        eventIsPlaceholder: hasAssetChanges || isNotBooked,
      };
    }

    const selectedStartTime = startDayjs.unix();

    const eventLocations = itineraryLocations.map((l) => ({
      ...l.location,
      order: l.order,
      travelTimeToNextLocation: l.totalTravelTimeToNextLocation,
      travelTimeToNextLocationOffset: 0,
      __typename: 'CalendarEventLocation' as const,
    }));

    const startTime = itineraryLocations?.[0]?.arrivalTimeEpoch || selectedStartTime;

    const end = itineraryLocations?.[itineraryLocations.length - 1]?.departureTimeEpoch || startTime + totalTime;

    const eventAssetIds = event?.assets?.map((a) => a.id) || [];
    const hasChanges = event.start !== startTime || event.end !== end
      || !isEqual(normalizedSelectedAssetIds, eventAssetIds);
    const isNotBooked = event.status !== EventStatuses.Booked;

    return {
      ...event,
      start: startTime,
      end,
      assets: selectedAssets,
      eventIsPlaceholder: hasChanges || isNotBooked,
      locations: eventLocations,
    };
  },
)

export const selectEventsWithChanges = createSelector(
  bookingScheduleFeature.selectBookingSchedule,
  selectPlaceholderEvent,
  (bookingSchedule, placeholderEvent) => {

    const events: ExtendedCalendarEventForScheduleFragment[] = [ ...bookingSchedule.events ];

    if (!placeholderEvent) { return events; }

    const index = events.findIndex((e) => e.id === placeholderEvent.id);
    if (index >= 0) {
      events[index] = placeholderEvent;
    } else {
      events.push(placeholderEvent);
    }

    return events;
  },
);

export const eventScheduleSelectors = {
    selectEventMissingLocations,
    selectEventsWithMissingAndInvalidLocations,
    selectAvailableTimes,
    selectAvailableAssets,
    selectTotalLocationTime,
    selectTotalTravelTime,
    selectTotalTime,
    selectStartTimeInterval,
    selectShowDockTravelTimeCheckbox,
    selectDefaultForIncludeDockTravel,
    selectItinerarySummary,
    selectEventItineraryNoLocations,
}
